import { FocusKeyManager, FocusOrigin } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  untracked,
  viewChild,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IconComponent } from '../icon';
import { ScrollableDirective } from '../scrollable';
import { TabLabelDirective } from './tab-label.directive';
import { TabComponent } from './tab.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-tab-group',
  exportAs: 'sbTabGroup',
  templateUrl: './tab-group.component.html',
  styleUrls: ['./tab-group.component.scss'],
  imports: [NgTemplateOutlet, TabLabelDirective, ScrollableDirective, IconComponent],
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.sb-tab-group]': 'true',
  },
})
export class TabGroupComponent {
  selectedIndex = model<number>(0);
  hideTabContent = input<boolean>(false);
  containHeight = input<boolean>(false);
  transparentBackground = input<boolean>(false);
  noPadding = input<boolean>(false);

  scrollable = viewChild<ScrollableDirective>(ScrollableDirective);

  tabs = contentChildren<TabComponent>(TabComponent, { descendants: true });
  labels = viewChildren<TabLabelDirective>(TabLabelDirective);

  private keyManager?: FocusKeyManager<TabLabelDirective>;

  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const labels = this.labels();
      untracked(() => {
        this.keyManager?.destroy();
        this.keyManager = undefined;
        this.setupKeyManager(labels);
      });
    });

    effect(() => {
      const selectedIndex = this.selectedIndex();
      untracked(() => {
        this.keyManager?.updateActiveItem(selectedIndex);
      });
    });

    this.destroyRef.onDestroy(() => {
      this.keyManager?.destroy();
    });
  }

  selectTabByIndex(index: number, focusOrigin: FocusOrigin = 'mouse'): void {
    if (this.selectedIndex() === index) return;

    this.keyManager?.setFocusOrigin(focusOrigin);
    this.keyManager?.setActiveItem(index);

    this.scrollable()?.scrollTo({ left: 0, top: 0 });
  }

  onKeyDown(event: KeyboardEvent): void {
    this.keyManager?.onKeydown(event);
  }

  private setupKeyManager(labels: readonly TabLabelDirective[]): void {
    this.keyManager = new FocusKeyManager(labels).withWrap().withHorizontalOrientation('ltr');
    this.keyManager.updateActiveItem(this.selectedIndex());
    this.keyManager.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((index) => {
      this.selectedIndex.set(index);
      this.tabs()[index]?.selected.emit();
    });
  }
}
